import React, {useState, useCallback, useEffect} from 'react';
import {useNavigate} from "react-router-dom";
import Box from '@mui/material/Box';
import Grid from "@mui/material/Grid";
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Typography from "@mui/material/Typography";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";

import {useAuth} from "../../_auth/useAuth";
import {useLocation} from "react-router-dom";
import {
    Form,
    DaButton,
    DaTextField,
    DaPasswordField,
    DaSelectField,
    ProgressPanel,
    MessagePanel,
    IMessagePanelProps,
    NumberFormatType,
    DaNumberFormatField,
    ButtonBar,
} from "../_shared";
import {FormPaper} from "./Signup.styles";

import {useTheme} from "@mui/material/styles";
import {signupFields, groupOptions} from "../../_appData";
import { AuthSignupOptions } from "../../_auth/types";

import {ISignup} from "../../_api";
import {AuthConfirmSignupOptions} from "../../_auth/types";
import {authenticationApi} from "../../_api";

interface loginState {
    returnTo: Location
};

const signupSchema: yup.SchemaOf<ISignup> = yup.object({
    first_name: yup.string().label(signupFields.firstName.label).max(150).required(),
    last_name: yup.string().label(signupFields.lastName.label).max(150).required(),
    email: yup.string().label(signupFields.email.label).max(254).required(),
    phone_number: yup.string().label(signupFields.phone.label).max(25).required(),
    password: yup.string().label(signupFields.password.label)
        .min(10, 'Password must be at least 10 characters long')
        .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
        .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .matches(/\d/, 'Password must contain at least one number')
        .matches(/[@\$!%\*\?&#<>\^\+=\|]/, 'Password must contain at least one special character (@$!%*?&#<>^+=|)')
        .required('Password is required'),
    confirm_password: yup.string().label(signupFields.confirmPassword.label)
        .oneOf([yup.ref("password"), null], "Passwords must match")
        .required(),
    groups: yup.string().label("Role")
        .when('email', {
            is: (email: string) => !email || !email.endsWith('@data-axle.com'),
            then: schema => schema.required(),
            otherwise: schema => schema.notRequired(),
        }),
    // officer_title: yup.string().label("Officer Title")
    //     .when('email', {
    //         is: (email: string) => email && email.endsWith('@data-axle.com'),
    //         then: schema => schema.required(),
    //         otherwise: schema => schema.notRequired(),
    //     }),
    // fax: yup.string().label("Fax")
    //     .when('email', {
    //         is: (email: string) => email && email.endsWith('@data-axle.com'),
    //         then: schema => schema.required(),
    //         otherwise: schema => schema.notRequired(),
    //     }),
    // federal_tax_id: yup.string().label("Federal Tax ID")
    //     .when('email', {
    //         is: (email: string) => email && email.endsWith('@data-axle.com'),
    //         then: schema => schema.required(),
    //         otherwise: schema => schema.notRequired(),
    //     }),
});

export const SignupView: React.FC = () => {
    const theme = useTheme();
    const { signup } = useAuth();
    const navigate = useNavigate();
    const [msgPanelProps, setMsgPanelProps] = useState<IMessagePanelProps>({message: ""});
    const [progressTitle, setProgressTitle] = useState<string>("Processing Signup Info...");
    const [isShowError, setIsShowError] = useState<boolean>(false);
    const [showProgress, setShowProgress] = useState<boolean>(false);
    const [groups, setGroups] = useState<string[]>([]);

    // legacy state values, need to refactor
    const { state } = useLocation();

    const formFieldSize = "medium";
    const formFieldVariant = "standard";

    // defining the initial state for the form
    const initialValues = {
        email: '',
        first_name: '',
        last_name: '',
        phone_number: '',
        password: '',
        confirm_password: '',
        groups: '',
    };

    const { control, handleSubmit, watch, formState: { errors } } = useForm<ISignup>({
        defaultValues: initialValues,
        resolver: yupResolver(signupSchema)
    });

    const emailValue = watch('email');
    const hideRoleField = emailValue && emailValue.endsWith('@data-axle.com');

    useEffect(() => {
        const fetchGroups = async () => {
            try {
                const resp = await authenticationApi.get_user_groups_oauth() as any;
                if (resp  && resp.groups) {
                    setGroups(resp.groups);
                }
            } catch {
            }
        };
        fetchGroups();
    }, []);

    const showError = ({title, message, type="error"}: IMessagePanelProps) => {
        const msgProps: IMessagePanelProps = {
            message: message,
            title: title,
            type: type
        }
        setMsgPanelProps(msgProps);
        setIsShowError(true);
    }

    // a submit function that will execute upon form submission
    const onSubmit: SubmitHandler<ISignup> = (data) => {
        submitSignupData(data);
    };

    const submitSignupData = useCallback(async (options: AuthSignupOptions): Promise<void> => {
        setProgressTitle("Saving Signup Info...");
        setShowProgress(true);

        let apiPromise = undefined;
        apiPromise = signup(options);

        apiPromise && apiPromise.then((resp) => {
            if (resp && resp.state === "success"){
                // get options to pass to confirmation route
                const { email, first_name, last_name, phone_number, groups} = options;
                let confirm_options: AuthConfirmSignupOptions = {
                    email: email,
                    first_name: first_name,
                    last_name: last_name,
                    phone_number: phone_number,
                    sub: resp.signupResult?.user_sub,
                    code: "",
                    group: groups
                    // officer_title: officer_title,
                    // fax: fax,
                    // federal_tax_id: federal_tax_id,
                }
                navigate("/signup-confirm", { state: confirm_options });
            } else {
                showError({
                    title: "Unable to Save Signup Info",
                    message: "An error occurred while saving Signup data. " +
                        "Verify that you have provided all required data and " +
                        "retry your last action."
                });
            }
        })
        .catch(() => {
            showError({
                title: "Signup API Save Error",
                message: "An error occurred while saving Signup data. " +
                    "There may have been a network error.",
            });
        })
        .finally(() => {
            setShowProgress(false);
        });
    }, [signup, navigate]);


    return (
        <>
            <FormPaper elevation={4}>
                {/* Form Title*/}
                {/*<FormTitle*/}
                {/*    title={"Sign Up"}*/}
                {/*    titleIcon={"account_circle_icon"}*/}
                {/*    typeVariant={"h4"}*/}
                {/*/>*/}
                <Box display="flex" flexDirection="column" alignItems="center">
                    <AccountCircleIcon
                        sx={{
                            fontSize: 68,
                            color: theme.palette.grey["500"],
                            mb: 2
                        }}
                    />
                    <Typography variant="h4">Sign Up</Typography>
                </Box>

                <Grid container>
                    {isShowError ? (
                        <Grid item xs={12} justifyContent={"start"}>
                            <MessagePanel
                                {...msgPanelProps}
                            />
                        </Grid>
                    ) : null}
                    {showProgress ? (
                        <Grid item xs={12}>
                            <ProgressPanel title={progressTitle}/>
                        </Grid>
                    ) : (
                        <Grid container item xs={12}>
                            <Form
                                autoComplete="off"
                                onSubmit={handleSubmit(onSubmit)}
                            >
                                <Grid container spacing={theme.spacing(1)}>
                                    <Grid item xs={12} md={12}>
                                        <DaTextField
                                            control={control}
                                            name={signupFields.firstName.name}
                                            label={signupFields.firstName.label}
                                            required={signupFields.firstName.required}
                                            variant={formFieldVariant}
                                            size={formFieldSize}
                                            errors={errors}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                        <DaTextField
                                            control={control}
                                            name={signupFields.lastName.name}
                                            label={signupFields.lastName.label}
                                            required={signupFields.lastName.required}
                                            variant={formFieldVariant}
                                            size={formFieldSize}
                                            errors={errors}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                        <DaTextField
                                            control={control}
                                            name={signupFields.email.name}
                                            label={signupFields.email.label}
                                            required={signupFields.email.required}
                                            variant={formFieldVariant}
                                            size={formFieldSize}
                                            errors={errors}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                        <DaNumberFormatField
                                            control={control}
                                            name={signupFields.phone.name}
                                            label={signupFields.phone.label}
                                            required={signupFields.phone.required}
                                            variant={formFieldVariant}
                                            size={formFieldSize}
                                            errors={errors}
                                            numberFormat={NumberFormatType.US_Phone}
                                        />
                                    </Grid>
                                    {!hideRoleField && (
                                      <Grid item xs={12} md={12}>
                                          <DaSelectField
                                              control={control}
                                              name="groups"
                                              label="Role"
                                              required={true}
                                              variant={formFieldVariant}
                                              size={formFieldSize}
                                              errors={errors}
                                              selectListItems={groups.map(g => ({ value: g, label: groupOptions[g as keyof typeof groupOptions] || g }))}
                                              showNone={false}
                                          />
                                      </Grid>
                                    )}
                                    <Grid item xs={12} md={12}>
                                        <DaPasswordField
                                            control={control}
                                            name={signupFields.password.name}
                                            label={signupFields.password.label}
                                            required={signupFields.password.required}
                                            variant={formFieldVariant}
                                            size={formFieldSize}
                                            errors={errors}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                        <DaPasswordField
                                            control={control}
                                            name={signupFields.confirmPassword.name}
                                            label={signupFields.confirmPassword.label}
                                            required={signupFields.confirmPassword.required}
                                            variant={formFieldVariant}
                                            size={formFieldSize}
                                            errors={errors}
                                        />
                                    </Grid>
                                </Grid>
                                <Grid container spacing={theme.spacing(1)}>
                                    <Grid item xs={12} md={12}>
                                        <ButtonBar buttonAlignment="alignRight">
                                            <DaButton buttonStyle="secondary" onClick={() => navigate("/login")}>
                                                Cancel
                                            </DaButton>
                                            <DaButton buttonStyle="primary" type="submit">
                                                Sign Up
                                            </DaButton>
                                        </ButtonBar>
                                    </Grid>
                                </Grid>
                            </Form>
                        </Grid>
                    )}
                </Grid>
            </FormPaper>
        </>
    );
}